import Phaser from 'phaser';
import { resendConfirmation, signIn, signUp } from '../../store/auth.js';
import { COLORS, FONT_HEADER, FONT_MONO } from '../style.js';
import { Button } from '../view/Button.js';
import { TextInputOverlay } from '../view/TextInputOverlay.js';
import { DESIGN_HEIGHT, DESIGN_WIDTH } from '../PhaserGame.js';

/**
 * Sign-in / sign-up form. One scene with a tab toggle at the top —
 * cheaper than two scenes when the form fields are identical, and
 * avoids re-mounting the inputs (which would lose any half-typed
 * value) when the user flips between modes.
 *
 * Phaser doesn't render text inputs natively, so the email / password
 * fields are HTML `<input>` elements via TextInputOverlay. The form
 * scaffolding (background, labels, buttons, status text) is all
 * Phaser. Coordinates stay in design canvas space; the overlay
 * helper handles the design → viewport scale translation.
 *
 * Slice 1.2a scope: sign in + sign up only. "Forgot password?"
 * lives at the bottom as a disabled placeholder until 1.2c wires
 * up the password-reset flow. Email confirmation gating + the
 * "Confirm your email" banner also land in 1.2c.
 */

type Mode = 'signin' | 'signup';

const PASSWORD_MIN = 8;

export class AuthScene extends Phaser.Scene {
  static readonly KEY = 'Auth';

  private mode: Mode = 'signin';
  private email = '';
  private password = '';
  private submitting = false;

  // Explicit `| undefined` (not `?:`) so we can reassign to undefined
  // under exactOptionalPropertyTypes — these fields toggle on/off as
  // the form transitions between active state and the sign-up-success
  // state.
  private emailInput: TextInputOverlay | undefined = undefined;
  private passwordInput: TextInputOverlay | undefined = undefined;
  private tabSigninText!: Phaser.GameObjects.Text;
  private tabSignupText!: Phaser.GameObjects.Text;
  private tabUnderline!: Phaser.GameObjects.Rectangle;
  private subtitleText!: Phaser.GameObjects.Text;
  private submitButton: Button | undefined = undefined;
  private statusText!: Phaser.GameObjects.Text;
  // Forgot-password link sits below the form on the sign-in tab and
  // hides on the sign-up tab (you wouldn't reset before you have an
  // account). Held as a field so switchMode can toggle visibility.
  private forgotPasswordLink: Phaser.GameObjects.Text | undefined = undefined;
  // Back-to-home button at the bottom of the form. We keep a ref so
  // showSignupSuccess can tear it down before drawing the success
  // state — otherwise we'd end up with two "Back to home" buttons
  // on screen at once.
  private backButton: Button | undefined = undefined;
  // Deep-link return target. When the user lands on /game/:id while
  // logged out, JoinScene routes here with the gameId so we can take
  // them back to the lobby after sign-in. Email-confirmation links
  // for sign-up include the gameId in the redirect URL so the round
  // trip preserves it too. Undefined for users who came in via the
  // normal Home → Sign In path.
  private returnToGameId: string | undefined = undefined;

  constructor() {
    super(AuthScene.KEY);
  }

  preload(): void {
    // Same horizontal wordmark JoinScene uses — the auth flow's
    // visual rhythm matches the lobby (both are pre-game routes).
    this.load.image('authLogo', '/assets/logo2.png');
  }

  init(data?: { returnToGameId?: string }): void {
    // Reset all per-instance state — scene.restart() bypasses the
    // constructor, so these MUST be re-initialized here or stale
    // values from a previous mount survive.
    this.mode = 'signin';
    this.email = '';
    this.password = '';
    this.submitting = false;
    this.emailInput = undefined;
    this.passwordInput = undefined;
    this.submitButton = undefined;
    this.forgotPasswordLink = undefined;
    this.backButton = undefined;
    this.returnToGameId = data?.returnToGameId;
  }

  create(): void {
    this.add
      .rectangle(
        DESIGN_WIDTH / 2,
        DESIGN_HEIGHT / 2,
        DESIGN_WIDTH,
        DESIGN_HEIGHT,
        COLORS.bgDeepInt
      )
      .setOrigin(0.5);

    this.drawLogo();
    this.drawHeader();
    this.drawTabs();
    this.drawForm();
    this.drawForgotPasswordLink();
    this.drawSubmit();
    this.drawStatus();
    this.drawBackButton();

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      // TextInputOverlay auto-cleans on shutdown, but the explicit
      // destroy() here makes the lifecycle obvious to readers.
      this.emailInput?.destroy();
      this.passwordInput?.destroy();
      this.submitButton?.destroy();
    });
  }

  // --- Static layout ---

  private drawLogo(): void {
    const logo = this.add.image(DESIGN_WIDTH / 2, 0, 'authLogo');
    const targetWidth = 540;
    const scale = Math.min(1, targetWidth / logo.width);
    logo.setScale(scale);
    const logoH = logo.height * scale;
    logo.setY(70 + logoH / 2);
  }

  private drawHeader(): void {
    // Title is fixed ("Welcome"); no need to keep a reference.
    this.add
      .text(DESIGN_WIDTH / 2, 320, 'Welcome', {
        fontFamily: FONT_HEADER,
        fontSize: '40px',
        color: COLORS.textPrimary,
        fontStyle: '700',
      })
      .setLetterSpacing(3)
      .setOrigin(0.5);

    this.subtitleText = this.add
      .text(DESIGN_WIDTH / 2, 370, 'Sign in to play multiplayer', {
        fontFamily: FONT_MONO,
        fontSize: '16px',
        color: COLORS.textMuted,
      })
      .setLetterSpacing(2)
      .setOrigin(0.5);
  }

  private drawTabs(): void {
    // Two text buttons separated by a thin divider. The underline
    // beneath the active tab is the visual indicator — slides over
    // (well, jumps for now; tween polish later if it feels static).
    const cx = DESIGN_WIDTH / 2;
    const y = 460;
    const gap = 200;

    this.tabSigninText = this.add
      .text(cx - gap / 2, y, 'SIGN IN', {
        fontFamily: FONT_HEADER,
        fontSize: '22px',
        color: COLORS.accentCyanHex,
        fontStyle: '700',
      })
      .setLetterSpacing(4)
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });

    this.tabSignupText = this.add
      .text(cx + gap / 2, y, 'SIGN UP', {
        fontFamily: FONT_HEADER,
        fontSize: '22px',
        color: COLORS.textMuted,
        fontStyle: '700',
      })
      .setLetterSpacing(4)
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });

    // Vertical divider between the two tabs — same hex grid steel
    // tone as the rest of the navigation chrome.
    this.add
      .rectangle(cx, y, 1, 24, COLORS.borderBrightInt, 0.6)
      .setOrigin(0.5);

    // Active-tab underline. Positioned beneath whichever tab is
    // selected; updated by switchMode().
    this.tabUnderline = this.add
      .rectangle(cx - gap / 2, y + 22, 110, 2, COLORS.accentCyanInt)
      .setOrigin(0.5);

    this.tabSigninText.on('pointerup', () => this.switchMode('signin'));
    this.tabSignupText.on('pointerup', () => this.switchMode('signup'));
  }

  private switchMode(next: Mode): void {
    if (this.mode === next) return;
    if (this.submitting) return;
    this.mode = next;

    // Visual: active tab gets cyan, inactive goes muted. Underline
    // slides under the active tab.
    const cx = DESIGN_WIDTH / 2;
    const gap = 200;
    if (next === 'signin') {
      this.tabSigninText.setColor(COLORS.accentCyanHex);
      this.tabSignupText.setColor(COLORS.textMuted);
      this.tabUnderline.setX(cx - gap / 2);
      this.subtitleText.setText('Sign in to play multiplayer');
      this.submitButton?.setText('Sign in');
      this.forgotPasswordLink?.setVisible(true);
    } else {
      this.tabSigninText.setColor(COLORS.textMuted);
      this.tabSignupText.setColor(COLORS.accentCyanHex);
      this.tabUnderline.setX(cx + gap / 2);
      this.subtitleText.setText('Create an account to play multiplayer');
      this.submitButton?.setText('Create account');
      // No "forgot password" before you've made an account — hide.
      this.forgotPasswordLink?.setVisible(false);
    }

    // Clear any prior error message; the new form is a fresh attempt.
    this.statusText.setText('');
  }

  private drawForm(): void {
    const cx = DESIGN_WIDTH / 2;
    const fieldWidth = 520;
    const fieldHeight = 64;

    // Email label
    this.add
      .text(cx - fieldWidth / 2, 568, 'EMAIL', {
        fontFamily: FONT_MONO,
        fontSize: '13px',
        color: COLORS.textSecondary,
        fontStyle: '700',
      })
      .setLetterSpacing(3)
      .setOrigin(0, 0.5);

    this.emailInput = new TextInputOverlay({
      scene: this,
      x: cx,
      y: 612,
      width: fieldWidth,
      height: fieldHeight,
      type: 'email',
      placeholder: 'you@example.com',
      autocomplete: 'email',
      maxLength: 254, // RFC 5321 max email length
      fontSizeDesign: 26,
      onChange: (v) => {
        this.email = v;
      },
    });

    // Password label
    this.add
      .text(cx - fieldWidth / 2, 696, 'PASSWORD', {
        fontFamily: FONT_MONO,
        fontSize: '13px',
        color: COLORS.textSecondary,
        fontStyle: '700',
      })
      .setLetterSpacing(3)
      .setOrigin(0, 0.5);

    this.passwordInput = new TextInputOverlay({
      scene: this,
      x: cx,
      y: 740,
      width: fieldWidth,
      height: fieldHeight,
      type: 'password',
      placeholder: 'At least 8 characters',
      autocomplete: 'current-password',
      maxLength: 128, // sane upper bound; Supabase doesn't impose one
      fontSizeDesign: 26,
      onChange: (v) => {
        this.password = v;
      },
      onSubmit: () => {
        void this.handleSubmit();
      },
    });
  }

  private drawForgotPasswordLink(): void {
    // Lives between the password field (y=740) and the submit button
    // (y=858). Mono caps to read as a tertiary action — quieter than
    // the cyan tab labels, louder than the back-button below.
    const link = this.add
      .text(DESIGN_WIDTH / 2, 808, 'FORGOT PASSWORD?', {
        fontFamily: FONT_MONO,
        fontSize: '13px',
        color: COLORS.accentCyanHex,
        fontStyle: '700',
      })
      .setLetterSpacing(3)
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    link.on('pointerup', () => {
      if (this.submitting) return;
      this.scene.start('ForgotPassword');
    });
    this.forgotPasswordLink = link;
    // sign-in is the default mode in create(), so the link starts
    // visible — no setVisible call needed here.
  }

  private drawSubmit(): void {
    this.submitButton = new Button(this, {
      x: DESIGN_WIDTH / 2,
      y: 858,
      width: 360,
      height: 80,
      text: 'Sign in',
      style: 'primary',
      onTap: () => {
        void this.handleSubmit();
      },
    });
  }

  private drawStatus(): void {
    // Inline status / error message. Hidden when empty. Colored
    // amber for "in flight" and red for "rejected."
    this.statusText = this.add
      .text(DESIGN_WIDTH / 2, 948, '', {
        fontFamily: FONT_MONO,
        fontSize: '15px',
        color: COLORS.accentRedHex,
        fontStyle: '700',
        align: 'center',
        wordWrap: { width: 560 },
      })
      .setLetterSpacing(1)
      .setOrigin(0.5);
  }

  private drawBackButton(): void {
    // Real button (default / non-primary style) — matches the "Home"
    // button pattern from GameOverModal so back navigation reads as
    // a deliberate action, not a discreet escape hatch. Primary
    // hierarchy stays with the submit button above; this one is
    // secondary but still clearly tappable on the dark backdrop.
    this.backButton = new Button(this, {
      x: DESIGN_WIDTH / 2,
      y: 1140,
      width: 240,
      height: 64,
      text: 'Back to home',
      onTap: () => {
        if (this.submitting) return;
        this.scene.start('Home');
      },
    });
  }

  // --- Form submission ---

  private async handleSubmit(): Promise<void> {
    if (this.submitting) return;

    const email = this.email.trim();
    const password = this.password;

    // Client-side validation. Server (Supabase) re-validates; this
    // is purely UX — keep the user out of the network round-trip
    // when the inputs obviously won't fly.
    if (!email) {
      this.setError('Enter your email.');
      this.emailInput?.focus();
      return;
    }
    if (!password) {
      this.setError('Enter your password.');
      this.passwordInput?.focus();
      return;
    }
    if (this.mode === 'signup' && password.length < PASSWORD_MIN) {
      this.setError(`Password must be at least ${PASSWORD_MIN} characters.`);
      this.passwordInput?.focus();
      return;
    }

    this.setSubmitting(true);
    // Return-path for both sign-up's confirmation email and sign-in's
    // post-success routing. /game/:id is the only deep link we
    // honor today; expand if other deep-link entry points appear.
    const returnPath = this.returnToGameId
      ? `/game/${this.returnToGameId}`
      : undefined;
    const result =
      this.mode === 'signin'
        ? await signIn(email, password)
        : await signUp(email, password, returnPath ? { returnTo: returnPath } : undefined);

    if (!result.ok) {
      this.setSubmitting(false);
      this.setError(result.error);
      return;
    }

    if (this.mode === 'signin') {
      // Sign-in succeeded → token in storage, onAuthStateChange has
      // already fired. If we came in via a /game/:id deep link,
      // bounce back there so the user lands in their lobby instead
      // of Home. JoinScene's own gate handles the case where the
      // user signed in but doesn't have a profile yet (→ routes
      // them through UsernameSetup, which knows the return path
      // too).
      if (this.returnToGameId) {
        this.scene.start('Join', { gameId: this.returnToGameId });
      } else {
        this.scene.start('Home');
      }
    } else {
      // Sign-up succeeded — but the user isn't actually signed in
      // yet (email confirmation required). Show a "check your email"
      // success state. The emailRedirectTo we passed in puts the
      // deep-link path (if any) into the confirmation link, so
      // clicking that lands them back at /game/:id signed-in.
      this.showSignupSuccess(email);
    }
  }

  private setSubmitting(submitting: boolean): void {
    this.submitting = submitting;
    this.emailInput?.setDisabled(submitting);
    this.passwordInput?.setDisabled(submitting);
    if (submitting) {
      this.statusText.setColor(COLORS.accentAmberHex);
      this.statusText.setText(
        this.mode === 'signin' ? 'Signing in…' : 'Creating account…'
      );
    }
  }

  private setError(msg: string): void {
    this.submitting = false;
    this.emailInput?.setDisabled(false);
    this.passwordInput?.setDisabled(false);
    this.statusText.setColor(COLORS.accentRedHex);
    this.statusText.setText(msg);
  }

  private showSignupSuccess(email: string): void {
    // Tear down the form chrome — the user is in a "wait for email"
    // state now and re-engaging with the form would be confusing.
    // Also tear down the bottom Back-to-home button + the forgot-
    // password link so they don't pile up alongside the success-
    // state buttons we're about to draw.
    this.emailInput?.destroy();
    this.emailInput = undefined;
    this.passwordInput?.destroy();
    this.passwordInput = undefined;
    this.submitButton?.destroy();
    this.submitButton = undefined;
    this.forgotPasswordLink?.destroy();
    this.forgotPasswordLink = undefined;
    this.backButton?.destroy();
    this.backButton = undefined;
    this.statusText.setText('');

    const cx = DESIGN_WIDTH / 2;

    this.add
      .text(cx, 580, 'Check your email', {
        fontFamily: FONT_HEADER,
        fontSize: '32px',
        color: COLORS.accentGreenHex,
        fontStyle: '700',
      })
      .setLetterSpacing(3)
      .setOrigin(0.5);

    this.add
      .text(
        cx,
        650,
        `We sent a confirmation link to\n${email}\n\nClick the link to finish setting up your account.`,
        {
          fontFamily: FONT_MONO,
          fontSize: '16px',
          color: COLORS.textPrimary,
          align: 'center',
          wordWrap: { width: 560 },
        }
      )
      .setLetterSpacing(1)
      .setOrigin(0.5);

    new Button(this, {
      x: cx,
      y: 860,
      width: 320,
      height: 76,
      text: 'Back to home',
      style: 'primary',
      onTap: () => this.scene.start('Home'),
    });

    // "Didn't get the email?" hint sits between Back-to-home and the
    // Resend button — gives the resend button context so users don't
    // tap it expecting something other than "send the same email again."
    this.add
      .text(cx, 935, "Didn't get it?", {
        fontFamily: FONT_MONO,
        fontSize: '13px',
        color: COLORS.textMuted,
      })
      .setLetterSpacing(2)
      .setOrigin(0.5);

    // Resend button — same inline-state pattern the HomeScene gate
    // banner uses. Label cycles through SENDING… → SENT → reset
    // after a few seconds; the closure-scoped `busy` flag prevents
    // re-tap during the cycle so we don't fire two resends back-to-
    // back (Supabase rate-limits anyway, but UI clarity wins).
    let busy = false;
    const returnPath = this.returnToGameId
      ? `/game/${this.returnToGameId}`
      : undefined;
    const resendButton = new Button(this, {
      x: cx,
      y: 1000,
      width: 320,
      height: 64,
      text: 'Resend confirmation email',
      onTap: () => {
        if (busy) return;
        busy = true;
        resendButton.setText('Sending…');
        void (async () => {
          const result = await resendConfirmation(
            email,
            returnPath ? { returnTo: returnPath } : undefined
          );
          if (result.ok) {
            resendButton.setText('Sent — check inbox');
            this.time.delayedCall(4000, () => {
              if (!resendButton) return;
              resendButton.setText('Resend confirmation email');
              busy = false;
            });
          } else {
            resendButton.setText('Failed — tap to retry');
            this.time.delayedCall(3000, () => {
              if (!resendButton) return;
              resendButton.setText('Resend confirmation email');
              busy = false;
            });
          }
        })();
      },
    });
  }
}
